import { MenuItem, Select, FormControl } from '@mui/material'
import get from 'lodash/get'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useActions } from '../../hooks/useActions'
import { useLocalization } from '../../redux/reduxHooks'
import { AlcoholGramsPerDrink } from '../../utils/AlcoholGramsPerDrink'
import {
  getLabel,
  pathToPerson,
  renderContent,
  calculateAlcoholConsumptionDaily,
} from '../../utils/helpers'
import { PersonFieldPaths as paths } from '../../utils/QuestionnaireStateManager'
import ConditionalSection from '../../widgets/ConditionalSection'
import { SubQuestionLabel } from '../../widgets/FormLabel'
import IntegerDetails from '../../widgets/IntegerDetails'

export const AlcoholType = ({ id, typeOfDrink }) => {
  const { setPersonTypeOfDrink } = useActions()

  return (
    <FormControl>
      <SubQuestionLabel>
        Which of the following drinks do you most frequently consume?
      </SubQuestionLabel>
      <Select
        value={typeOfDrink}
        onChange={({ target: { value } }) => {
          if (value !== typeOfDrink) {
            setPersonTypeOfDrink({ id, value })
          }
        }}
        inputProps={{ 'aria-label': 'select menu for type of alcohol' }}
      >
        {AlcoholGramsPerDrink.map(({ id: drinkId, label }) => {
          return (
            <MenuItem key={drinkId} value={drinkId}>
              {label ?? drinkId}
            </MenuItem>
          )
        })}
      </Select>
    </FormControl>
  )
}

export const AlcoholDrinks = ({ id, drinkAmount }) => {
  const { setPersonDrinkAmount, setPersonDrinkingFrequency } = useActions()

  const drinkingFrequency =
    useSelector((state) =>
      get(state, [
        ...pathToPerson(id),
        'cancerRiskData',
        'alcoholConsumptionDetails',
        0,
        'drinkingFrequency',
      ]),
    ) || ''

  const alcoholDrinkLabel = 'On average, how many drinks do you have each week?'

  return (
    <FormControl>
      <SubQuestionLabel>{alcoholDrinkLabel}</SubQuestionLabel>
      <IntegerDetails
        label={alcoholDrinkLabel}
        value={drinkAmount}
        onChange={(value) => {
          setPersonDrinkAmount({ id, value })
          if (!drinkingFrequency && value) {
            setPersonDrinkingFrequency({ id, value: 'weekly' })
          }
        }}
      />
    </FormControl>
  )
}

const Alcohol = ({ id, patient, config }) => {
  const localization = useLocalization()
  const {
    setPersonConsumesAlcohol,
    removePersonCancerRiskProperty,
    setAlcoholConsumptionInGramsPerDay,
  } = useActions()
  const consumesAlcohol = useSelector((state) =>
    get(state, [...pathToPerson(id), 'cancerRiskData', 'consumesAlcohol']),
  )

  const typeOfDrink =
    useSelector((state) =>
      get(state, [
        ...pathToPerson(id),
        'cancerRiskData',
        'alcoholConsumptionDetails',
        0,
        'typeOfDrink',
      ]),
    ) || ''

  const drinkAmount =
    useSelector((state) =>
      get(state, [
        ...pathToPerson(id),
        'cancerRiskData',
        'alcoholConsumptionDetails',
        0,
        'drinkAmount',
      ]),
    ) || ''

  useEffect(() => {
    if (consumesAlcohol && typeOfDrink && drinkAmount) {
      const gramPerDay = calculateAlcoholConsumptionDaily(parseFloat(drinkAmount), typeOfDrink)
      setAlcoholConsumptionInGramsPerDay({ id, value: gramPerDay.toString() })
    } else {
      setAlcoholConsumptionInGramsPerDay({ id, value: undefined })
    }
  }, [consumesAlcohol, drinkAmount, typeOfDrink])

  let conditionState

  switch (consumesAlcohol) {
    case true:
      conditionState = 'Y'
      break
    case false:
      conditionState = 'N'
      break
    default:
      conditionState = null
  }

  return (
    <ConditionalSection
      label={getLabel(localization, paths.ALCOHOL_USAGE_LABEL, patient)}
      conditionState={conditionState}
      onConditionStateChange={(value) => {
        if (value !== consumesAlcohol) {
          setPersonConsumesAlcohol({ id, value: value === 'Y' })
        }
      }}
      cleanUpChildQuestions={async () => {
        removePersonCancerRiskProperty({ id, type: 'alcoholConsumptionDetails' })
      }}
      showWhenCondition="Y"
      orderSchema={['Y', 'N']}
    >
      {renderContent({
        renderMap: {
          alcoholType: <AlcoholType key="at" id={id} typeOfDrink={typeOfDrink} />,
          alcoholAmount: (
            <AlcoholDrinks
              key="am"
              id={id}
              disabled={!typeOfDrink}
              disabledMinus={!typeOfDrink}
              disabledPlus={!typeOfDrink}
              drinkAmount={drinkAmount}
            />
          ),
        },
        config,
      })}
    </ConditionalSection>
  )
}

export default Alcohol
